import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Home, BookOpen, Upload, Users, BarChart3, Calendar,
  Layers, MessageCircle, Star, Award, X, Zap, Mail, ShieldCheck, FlaskConical,
  Sparkles, Database, ChevronDown, ChevronRight, Trash2, History
} from 'lucide-react';
import { api } from '../../api';

export const Sidebar = ({ user, activeView, setActiveView, isOpen, onClose }) => {
  const { t } = useTranslation();
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingLabCount, setPendingLabCount] = useState(0);
  const [pendingSyndicationCount, setPendingSyndicationCount] = useState(0);
  const [hasNewCourses, setHasNewCourses] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_collapsed_sections') || '{}'); } catch { return {}; }
  });

  const toggleSection = (key) => {
    setCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('sidebar_collapsed_sections', JSON.stringify(next));
      return next;
    });
  };

  // Fetch admin pending counts
  useEffect(() => {
    if (['admin', 'ms_stakeholder'].includes(user?.role)) {
      api.request('/private-workshop-requests/pending-count').then(d => setPendingCount(d.count || 0)).catch(() => {});
      api.request('/lab-requests/pending-count').then(d => setPendingLabCount(d.count || 0)).catch(() => {});
      api.request('/syndication-requests/pending-count').then(d => setPendingSyndicationCount(d.count || 0)).catch(() => {});
    }
  }, [user?.role, activeView]);

  // Fetch new-courses badge (7-day window)
  useEffect(() => {
    api.request('/courses/new-count').then(({ count, latest_date }) => {
      if (count > 0 && latest_date) {
        const lastSeen = localStorage.getItem('whats_new_last_seen');
        const isNew = !lastSeen || new Date(latest_date) > new Date(lastSeen);
        setHasNewCourses(isNew);
      }
    }).catch(() => {});
  }, []);

  // Clear badge when user visits whats-new
  useEffect(() => {
    if (activeView === 'whats-new') {
      localStorage.setItem('whats_new_last_seen', new Date().toISOString());
      setHasNewCourses(false);
    }
  }, [activeView]);

  const menuGroups = {
    training_partner: [
      { title: 'Overview', items: [
        { id: 'home', labelKey: 'nav.home', icon: Home },
        { id: 'courses', labelKey: 'nav.browseCourses', icon: BookOpen },
        { id: 'whats-new', labelKey: 'nav.whatsNew', icon: Sparkles },
      ]},
      { title: 'Delivery', items: [
        { id: 'my-workshops', labelKey: 'nav.myWorkshops', icon: Calendar },
        { id: 'my-lab-requests', labelKey: 'nav.myLabRequests', icon: FlaskConical },
        { id: 'workshop-planner', labelKey: 'nav.workshopPlanner', icon: Zap },
      ]},
      { title: 'Insights', items: [
        { id: 'my-analytics', labelKey: 'nav.myAnalytics', icon: BarChart3 },
      ]},
    ],
    partner_admin: [
      { title: 'Overview', items: [
        { id: 'home', labelKey: 'nav.home', icon: Home },
        { id: 'courses', labelKey: 'nav.browseCourses', icon: BookOpen },
        { id: 'whats-new', labelKey: 'nav.whatsNew', icon: Sparkles },
      ]},
      { title: 'Team', items: [
        { id: 'trainer-management', labelKey: 'nav.trainerManagement', icon: Users },
      ]},
      { title: 'Delivery', items: [
        { id: 'my-workshops', labelKey: 'nav.myWorkshops', icon: Calendar },
        { id: 'my-lab-requests', labelKey: 'nav.myLabRequests', icon: FlaskConical },
        { id: 'workshop-planner', labelKey: 'nav.workshopPlanner', icon: Zap },
      ]},
      { title: 'Requests', items: [
        { id: 'my-workshop-requests', labelKey: 'nav.myPrivateWorkshopRequests', icon: Award },
        { id: 'my-syndication-requests', labelKey: 'nav.mySyndicationRequests', icon: Database },
      ]},
      { title: 'Insights', items: [
        { id: 'partner-analytics', labelKey: 'nav.analytics', icon: BarChart3 },
      ]},
    ],
    content_admin: [
      { title: 'Overview', items: [
        { id: 'home', labelKey: 'nav.home', icon: Home },
        { id: 'courses', labelKey: 'nav.browseCourses', icon: BookOpen },
        { id: 'whats-new', labelKey: 'nav.whatsNew', icon: Sparkles },
      ]},
      { title: 'Content', items: [
        { id: 'manage-courses', labelKey: 'nav.manageCourses', icon: Layers },
        { id: 'upload', labelKey: 'nav.uploadContent', icon: Upload },
      ]},
      { title: 'Insights', items: [
        { id: 'analytics', labelKey: 'nav.contentPerformance', icon: BarChart3 },
      ]},
    ],
    ms_stakeholder: [
      { title: 'Overview', items: [
        { id: 'home', labelKey: 'nav.home', icon: Home },
        { id: 'courses', labelKey: 'nav.browseCourses', icon: BookOpen },
        { id: 'whats-new', labelKey: 'nav.whatsNew', icon: Sparkles },
      ]},
      { title: 'Insights', items: [
        { id: 'analytics', labelKey: 'nav.analytics', icon: BarChart3 },
        { id: 'workshops', labelKey: 'nav.allWorkshops', icon: Calendar },
      ]},
      { title: 'Requests', items: [
        { id: 'workshop-requests', labelKey: 'nav.privateDeliveryRequests', icon: Award },
        { id: 'syndication-requests', labelKey: 'nav.syndicationRequests', icon: Database },
        { id: 'lab-requests', labelKey: 'nav.labRequests', icon: FlaskConical },
      ]},
    ],
    admin: [
      { title: 'Overview', items: [
        { id: 'home', labelKey: 'nav.home', icon: Home },
        { id: 'courses', labelKey: 'nav.browseCourses', icon: BookOpen },
        { id: 'whats-new', labelKey: 'nav.whatsNew', icon: Sparkles },
      ]},
      { title: 'Content', items: [
        { id: 'manage-courses', labelKey: 'nav.manageCourses', icon: Layers },
        { id: 'upload', labelKey: 'nav.uploadContent', icon: Upload },
      ]},
      { title: 'Requests', items: [
        { id: 'workshops', labelKey: 'nav.allWorkshops', icon: Calendar },
        { id: 'workshop-requests', labelKey: 'nav.privateDeliveryRequests', icon: Award },
        { id: 'syndication-requests', labelKey: 'nav.syndicationRequests', icon: Database },
        { id: 'lab-requests', labelKey: 'nav.labRequests', icon: FlaskConical },
      ]},
      { title: 'Insights', items: [
        { id: 'analytics', labelKey: 'nav.analytics', icon: BarChart3 },
        { id: 'chatbot-insights', labelKey: 'nav.chatbotInsights', icon: MessageCircle },
        { id: 'feedback-analytics', labelKey: 'nav.feedbackAnalytics', icon: Star },
      ]},
      { title: 'Administration', items: [
        { id: 'users', labelKey: 'nav.userManagement', icon: Users },
        { id: 'email-settings', labelKey: 'nav.emailSettings', icon: Mail },
        { id: 'access-control', labelKey: 'nav.accessControl', icon: ShieldCheck },
        { id: 'deleted-items', labelKey: 'nav.deletedItems', icon: Trash2 },
        { id: 'account-audit', labelKey: 'nav.accountAudit', icon: History },
      ]},
    ]
  };

  const groups = menuGroups[user?.role] || menuGroups.training_partner;

  const renderBadges = (id) => (
    <>
      {id === 'whats-new' && hasNewCourses && (
        <span className="relative flex h-2.5 w-2.5" data-testid="whats-new-badge">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
        </span>
      )}
      {id === 'workshop-requests' && pendingCount > 0 && (
        <span className="bg-yellow-400 text-levelup-darker text-xs font-bold px-2 py-0.5 rounded-full">{pendingCount}</span>
      )}
      {id === 'lab-requests' && pendingLabCount > 0 && (
        <span className="bg-orange-400 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pendingLabCount}</span>
      )}
      {id === 'syndication-requests' && pendingSyndicationCount > 0 && (
        <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pendingSyndicationCount}</span>
      )}
    </>
  );

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      <aside className={`fixed lg:static lg:h-full lg:overflow-y-auto inset-y-0 left-0 z-50 w-56 bg-levelup-darker transform transition-transform lg:transform-none ${
        isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="h-full flex flex-col">
          <div className="p-3 border-b border-levelup-teal lg:hidden">
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-levelup-teal text-white float-right">
              <X size={22} />
            </button>
          </div>
          <div className="px-3 py-2.5 border-b border-levelup-teal">
            <h2 className="text-levelup-accent font-bold text-base leading-tight">Skilling in a Box<sup className="text-[10px] font-semibold ml-1 align-super opacity-80">Beta</sup></h2>
            <p className="text-gray-400 text-[11px] mt-0.5 leading-snug">Enabling partners to scale skilling with velocity</p>
          </div>
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
            {groups.map(group => {
              const key = `${user?.role}:${group.title}`;
              const isCollapsed = !!collapsed[key];
              return (
                <div key={group.title}>
                  <button
                    onClick={() => toggleSection(key)}
                    data-testid={`nav-section-${group.title.toLowerCase().replace(/\s/g, '-')}`}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    <span>{group.title}</span>
                    {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                  </button>
                  {!isCollapsed && (
                    <div className="space-y-0.5">
                      {group.items.map(item => (
                        <button
                          key={item.id}
                          onClick={() => { setActiveView(item.id); onClose(); }}
                          data-testid={`nav-${item.id}`}
                          className={`w-full flex items-center space-x-2.5 px-3 py-2 rounded-lg transition-all ${
                            activeView === item.id
                              ? 'bg-levelup-accent text-levelup-darker'
                              : 'text-gray-300 hover:bg-levelup-teal hover:text-white'
                          }`}
                        >
                          <item.icon size={17} className="flex-shrink-0" />
                          <span className="font-medium text-sm flex-1 text-left">{t(item.labelKey)}</span>
                          {renderBadges(item.id)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
};
